import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PriceSummary } from '@paperxyz/js-client-sdk';
import { CheckoutWithCard } from '@paperxyz/react-client-sdk';
import { faQuestionCircle } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { theme } from '@components-shared/src/stitches';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { Tooltip } from '@components/main-components/tooltip/Tooltip';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { BuyEditionSongDetailsFragmentDoc, getFragment } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import type { PlaylistReferral } from '@utils/types';
import { PaperProcessingModal } from '../../../PaperProcessingModal';
import { SongDetails } from '../CreditCardCheckoutSongDetails';
import { CREDIT_CARD_FEE_INFO_COPY } from '../credit-card-helpers';

export const PaperCCMintModal = ({
  paperKey,
  quantity,
  data,
  onSuccessfulTransaction,
  selectedTier,
  playlistReferral,
}: {
  paperKey: string;
  quantity: number;
  data: FragmentType<BuyEditionSongDetailsFragmentDoc>;
  onSuccessfulTransaction: () => void;
  selectedTier: number | null;
  playlistReferral: PlaylistReferral | null;
}) => {
  const { closeCancelModal, openModal } = ModalContext.useContainer();
  const logError = useLogError();
  const { tabletLandscape } = MediaQuery.useContainer();

  const release = getFragment(BuyEditionSongDetailsFragmentDoc, data);

  const [breakdown, setBreakdown] = React.useState<PriceSummary>();

  const fees =
    ((breakdown?.networkFees.valueInSubunits || 0) +
      (breakdown?.serviceFees.valueInSubunits || 0)) /
    100;
  const totalPrice = (breakdown?.total.valueInSubunits || 0) / 100;
  const editionPrice = totalPrice - fees;

  return (
    <StandardModal
      body={
        <>
          <SongDetails data={data} selectedTier={selectedTier} />
          {breakdown && (
            <div className="my-6 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <p className="text-base-m font-medium text-base800">Edition x {quantity}</p>
                <p className="text-base-m text-base600">${editionPrice.toFixed(2)}</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-base-m font-medium text-base800">
                  <span>Processing Fees</span>
                  <Tooltip openOnClick={!tabletLandscape}>
                    <Tooltip.Trigger>
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        fontSize={12}
                        className="ml-1 text-base500"
                      />
                    </Tooltip.Trigger>
                    <Tooltip.StyledContent collisionPadding={{ left: 10 }}>
                      <p className="max-w-xs text-base-s ">{CREDIT_CARD_FEE_INFO_COPY}</p>
                    </Tooltip.StyledContent>
                  </Tooltip>
                </p>
                <p className="text-base-m text-base600">${fees.toFixed(2)}</p>
              </div>
              <div className="flex items-center justify-between font-title text-title-s font-medium text-base800">
                <p>Total Price</p>
                <p>${totalPrice.toFixed(2)}</p>
              </div>
            </div>
          )}
          <div
            className="[&>div]:h-auto"
            onScroll={e => {
              e?.preventDefault();
              e?.stopPropagation();
            }}
            onTouchMove={e => {
              e?.preventDefault();
              e?.stopPropagation();
            }}
          >
            <CheckoutWithCard
              onPriceUpdate={setBreakdown}
              sdkClientSecret={paperKey}
              onPaymentSuccess={result => {
                onSuccessfulTransaction();
                openModal(ModalType.PAPER_PROCESSING, {
                  body: (
                    <PaperProcessingModal
                      receiptId={result.id}
                      releaseId={release.id}
                      chainId={release.chainId}
                      playlistReferral={playlistReferral}
                    />
                  ),
                });
              }}
              onError={paperError => {
                logError({
                  action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
                  error: paperError.error,
                  closeProcessingModal: true,
                  message: 'Error while buying with credit card',
                  errorType: ERROR_TYPE.PAPER_CC,
                  pillar: PILLARS.MINTING,
                  isArtistOnlyPage: false,
                  level: 'fatal',
                  toast: 'Error while buying with credit card',
                  indexedTags: {
                    releaseId: release.id,
                    chainId: release.chainId,
                    paperErrorCode: paperError.code,
                  },
                });
                // something went wrong, let's close the modal
                closeCancelModal();
              }}
              options={{
                colorBackground: theme.colors.white.value,
                colorPrimary: theme.colors.black.value,
                colorText: theme.colors.base800.value,
                borderRadius: 6,
              }}
            />
          </div>
        </>
      }
      title="Pay with Card"
      closeModal={closeCancelModal}
    />
  );
};
